import { useState } from "react";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { sendNavigationLinkEvent } from "~/app/analytics/ga/ga";
import type { TreeNode } from "~/domains/navigation/types/NavigationTypes";

export interface NavigationState {
  selectedNodes: TreeNode[];
  nodesInTransition: TreeNode[];
  isOpen: boolean;
  selectNode: (node: TreeNode, noChildren?: boolean) => void;
  goBack: () => void;
  clearNodes: () => void;
  flushTransitionNodes: () => void;
  close: () => void;
  closeOnly: () => void;
  toggleOpen: () => void;
}

export const useNavigationState = (rootNode: TreeNode): NavigationState => {
  const { val: isOpen, setFalse: close, setTrue: open, toggle } = useBooleanState(false);
  const [selectedNodes, setSelectedNodes] = useState<TreeNode[]>([rootNode]);
  const [nodesInTransition, setNodesInTransition] = useState<TreeNode[]>([rootNode]);

  const selectNode = (node: TreeNode, noChildren = false) => {
    const lastNode = nodesInTransition[nodesInTransition.length - 1];

    if (!isOpen) {
      open();
    }

    if (!noChildren) {
      sendNavigationLinkEvent(node.gaLabel, node.level);

      setSelectedNodes([...nodesInTransition, node]);
    } else if (lastNode.level >= node.level) {
      setSelectedNodes([...nodesInTransition.slice(0, -1)]);
    }

    if (node.childNodes.length !== 0) {
      setNodesInTransition((n) => {
        const targetIndex = node.level + 1;
        const copy = n.slice(0, targetIndex);
        copy.push(node);
        return copy;
      });
    }
  };

  const goBack = () => {
    if (nodesInTransition.length > 1) {
      setNodesInTransition(nodesInTransition.slice(0, -1));
    }
  };

  const clearNodes = () => {
    setNodesInTransition([rootNode]);
    setSelectedNodes([rootNode]);
  };

  const handleTransitionEnd = () => {
    setSelectedNodes(nodesInTransition);
  };

  const handleClickOutside = () => {
    close();
    clearNodes();
  };

  const handleCloseOnly = () => {
    close();
  };

  return {
    selectedNodes,
    nodesInTransition,
    isOpen,
    selectNode,
    goBack,
    clearNodes,
    flushTransitionNodes: handleTransitionEnd,
    close: handleClickOutside,
    closeOnly: handleCloseOnly,
    toggleOpen: toggle,
  };
};
